import { Button } from '../base/Button';

interface PreviewBarTrackInfo {
  cover?: string | null;
  title: string;
  releaseTitle?: string;
  artist?: string;
  subtitle?: string;
}

interface PreviewBarProps {
  track: PreviewBarTrackInfo;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onAddToCart?: () => void;
  onTip?: () => void;
  onClose: () => void;
  tipEnabled?: boolean;
  actionLabel?: string;
}

const DEFAULT_COVER = '/images/254albumdefault_sm.png';

export const PreviewBar = ({
  track,
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onAddToCart,
  onTip,
  onClose,
  tipEnabled,
  actionLabel,
}: PreviewBarProps) => {
  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage =
    duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-3 min-w-0 w-full sm:flex-1">
            <img
              src={track.cover ?? DEFAULT_COVER}
              alt={`${track.title} artwork`}
              className="w-12 h-12 rounded object-cover object-top flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 whitespace-normal sm:truncate">
                {track.title}
              </p>
              {track.releaseTitle && (
                <p className="text-xs text-gray-500 whitespace-normal sm:truncate">
                  from {track.releaseTitle}
                </p>
              )}
              {track.artist && (
                <p className="text-sm text-gray-600 whitespace-normal sm:truncate">
                  {track.artist}
                </p>
              )}
              {track.subtitle && (
                <p className="text-xs text-gray-500 whitespace-normal sm:truncate">
                  {track.subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 w-full sm:w-auto sm:flex-1 sm:justify-center sm:max-w-xl">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <button
                onClick={onPlayPause}
                className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors cursor-pointer"
                aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
              >
                <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`} />
              </button>

              <div className="flex items-center space-x-2 flex-1 min-w-0 max-w-md">
                <span className="text-xs text-gray-500 w-10 text-right">
                  {formatTime(currentTime)}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-1 relative min-w-[120px]">
                  <div
                    className="bg-emerald-600 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-10">{formatTime(duration)}</span>
              </div>
            </div>

            <div className="flex items-center space-x-2 justify-end">
            {tipEnabled && onTip && (
              <Button variant="ghost" size="sm" onClick={onTip} className="hidden sm:flex">
                <i className="ri-heart-line mr-1" />
                Tip
              </Button>
            )}
            {onAddToCart && (
              <Button variant="primary" size="sm" onClick={onAddToCart}>
                <i className="ri-shopping-cart-line mr-1" />
                <span className="hidden sm:inline">{actionLabel ?? 'Buy'}</span>
                <span className="sm:hidden">Buy</span>
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              aria-label="Close preview"
            >
              <i className="ri-close-line" />
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
